// Utility functions for evaluating conditional logic in policy forms
import { isEmptyValue, isValidComparisonOperator, isValidLogicalOperator } from './utils';
import { calculateAgeFromBirthDate } from '../calculations/demographics/calculateAgeFromBirthDate';

// Interface for form parameters (query params, etc.)
export interface FormParameterValues {
  [key: string]: string | number | boolean;
}

function tryCalculateAge(value: string): number | null {
  try {
    const datePattern = /^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}$/;
    if (!datePattern.test(value)) {
      return null;
    }

    const age = calculateAgeFromBirthDate(value);
    return isNaN(age) ? null : age;
  } catch (error) {
    return null;
  }
}

function toNumber(
  value: PolicyFormSubmissionDataValue | PolicyFormSubmissionDataValue[] | undefined | null,
): number | null {
  if (value === undefined || value === null) return null;

  if (typeof value === 'number') return value;

  if (typeof value === 'string') {
    const age = tryCalculateAge(value);
    if (age !== null) {
      return age;
    }

    // If not a date, try direct number parsing
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) return parsed;

    return null;
  }

  // Other types (boolean, array, object) cannot be converted
  return null;
}

function isFieldCondition(condition: Condition): condition is FieldCondition {
  if (!('field' in condition && 'operator' in condition)) return false;

  // Validate that the operator is a valid ComparisonOperator
  return (
    typeof condition.field === 'string' && isValidComparisonOperator((condition as any).operator)
  );
}

function isLogicalCondition(condition: Condition): condition is LogicalCondition {
  if (!('operator' in condition && 'conditions' in condition)) return false;

  // Validate that the operator is a valid logical operator and conditions is an array
  return (
    isValidLogicalOperator((condition as any).operator) &&
    Array.isArray((condition as any).conditions)
  );
}

function evaluateFieldCondition(
  condition: FieldCondition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): boolean {
  try {
    // Check form data first, then fall back to parameters
    const fieldValue = formData[condition.field] ?? parameters[condition.field];

    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;

      case 'notEquals':
        return fieldValue !== condition.value;

      case 'empty':
        return isEmptyValue(fieldValue);

      case 'notEmpty':
        return !isEmptyValue(fieldValue);

      case 'contains':
        if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
          return fieldValue.includes(condition.value);
        }
        return false;

      case 'notContains':
        if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
          return !fieldValue.toLowerCase().includes(condition.value.toLowerCase());
        }
        // Handle array values (like ['owner_client', 'owner_joint'])
        if (Array.isArray(condition.value)) {
          return !condition.value.some(value => {
            if (typeof fieldValue === 'string' && typeof value === 'string') {
              return fieldValue.toLowerCase().includes(value.toLowerCase());
            }
            return fieldValue === value;
          });
        }
        return true;

      case 'in':
        if (Array.isArray(condition.value)) {
          return condition.value.some(value => value === fieldValue);
        }
        return false;

      case 'notIn':
        if (Array.isArray(condition.value)) {
          return !condition.value.some(value => value === fieldValue);
        }
        return true;

      case 'greaterThan': {
        const fieldNum = toNumber(fieldValue);
        const conditionNum = toNumber(condition.value);
        // If field value is not initialized or cannot be converted, treat as false (hide the field)
        if (fieldNum === null || conditionNum === null) {
          return false;
        }
        return fieldNum > conditionNum;
      }

      case 'greaterThanOrEqual': {
        const fieldNum = toNumber(fieldValue);
        const conditionNum = toNumber(condition.value);
        // If field value is not initialized or cannot be converted, treat as false (hide the field)
        if (fieldNum === null || conditionNum === null) {
          return false;
        }
        return fieldNum >= conditionNum;
      }

      case 'lessThan': {
        const fieldNum = toNumber(fieldValue);
        const conditionNum = toNumber(condition.value);
        // If field value is not initialized or cannot be converted, treat as false (hide the field)
        if (fieldNum === null || conditionNum === null) {
          return false;
        }
        return fieldNum < conditionNum;
      }

      case 'lessThanOrEqual': {
        const fieldNum = toNumber(fieldValue);
        const conditionNum = toNumber(condition.value);
        // If field value is not initialized or cannot be converted, treat as false (hide the field)
        if (fieldNum === null || conditionNum === null) {
          return false;
        }
        return fieldNum <= conditionNum;
      }

      default:
        console.warn('Unknown field condition operator:', condition.operator);
        return true;
    }
  } catch (error) {
    console.error('Error evaluating field condition:', error, condition);
    return true; // Default to showing the field on error
  }
}

export function evaluateCondition(
  condition: Condition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): boolean {
  try {
    if (isFieldCondition(condition)) {
      return evaluateFieldCondition(condition, formData, parameters);
    }

    if (isLogicalCondition(condition)) {
      if (condition.operator === 'and') {
        return condition.conditions.every(c => evaluateCondition(c, formData, parameters));
      } else if (condition.operator === 'or') {
        return condition.conditions.some(c => evaluateCondition(c, formData, parameters));
      }
    }

    console.warn('Invalid condition structure:', condition);
    return true; // Default to showing the field for invalid conditions
  } catch (error) {
    console.error('Error evaluating condition:', error, condition);
    return true; // Default to showing the field on error
  }
}

export function evaluateConditionalBehavior(
  conditional: ConditionalBehavior | undefined,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
  defaultRequired = false,
  hiddenDefault = false,
): ConditionalEvaluationResult {
  try {
    if (!conditional) {
      return {
        visible: true && !hiddenDefault,
        required: defaultRequired,
        showConditionalText: false,
      };
    }

    const visible = hiddenDefault
      ? false
      : conditional.visible
        ? evaluateCondition(conditional.visible, formData, parameters)
        : true;

    // If not visible, it can't be required and text condition doesn't matter
    if (!visible) {
      return {
        visible: false,
        required: false,
        showConditionalText: false,
      };
    }

    // If visible, evaluate required condition if present
    const required = conditional.required
      ? evaluateCondition(conditional.required, formData, parameters)
      : defaultRequired;

    // Evaluate text condition if present (for conditionalTitle and conditionalHelperText)
    const showConditionalText = conditional.showConditionalText
      ? evaluateCondition(conditional.showConditionalText, formData, parameters)
      : false;

    return {
      visible,
      required,
      showConditionalText,
    };
  } catch (error) {
    console.error('Error evaluating conditional behavior:', error, conditional);
    return {
      visible: true, // Default to visible on error
      required: defaultRequired,
      showConditionalText: false,
    };
  }
}

// Helper function to filter visible questions from a group
export function filterVisibleQuestions(
  questionRefs: string[],
  questionRegistry: Record<string, PolicyFormQuestion>,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): string[] {
  return questionRefs.filter(questionRef => {
    const question = questionRegistry[questionRef];
    if (!question) return false;

    const { visible } = evaluateConditionalBehavior(
      question.conditional,
      formData,
      parameters,
      question.required,
    );

    return visible;
  });
}

// Helper function to check if a question group should be visible
export function isQuestionGroupVisible(
  group: PolicyFormQuestionGroup,
  questionRegistry: Record<string, PolicyFormQuestion>,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): boolean {
  // First check the group's own conditional
  const groupResult = evaluateConditionalBehavior(group.conditional, formData, parameters);
  if (!groupResult.visible) return false;

  // Then check if at least one question in the group is visible
  const visibleQuestions = filterVisibleQuestions(
    group.questions,
    questionRegistry,
    formData,
    parameters,
  );

  return visibleQuestions.length > 0;
}

// Helper function to check if a subsection should be visible
export function isSubsectionVisible(
  subsection: PolicyFormSubSection,
  questionRegistry: Record<string, PolicyFormQuestion>,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): boolean {
  // First check the subsection's own conditional
  const subsectionResult = evaluateConditionalBehavior(
    subsection.conditional,
    formData,
    parameters,
  );
  if (!subsectionResult.visible) return false;

  // Then check if at least one question group in the subsection is visible
  return subsection.questionGroups.some(group =>
    isQuestionGroupVisible(group, questionRegistry, formData, parameters),
  );
}

// Helper function to check if a section should be visible
export function isSectionVisible(
  section: PolicyFormSection,
  questionRegistry: Record<string, PolicyFormQuestion>,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): boolean {
  // First check the section's own conditional
  const sectionResult = evaluateConditionalBehavior(section.conditional, formData, parameters);
  if (!sectionResult.visible) return false;

  // Then check if at least one subsection in the section is visible
  return section.subsections.some(subsection =>
    isSubsectionVisible(subsection, questionRegistry, formData, parameters),
  );
}

// ==================== Table Column Conditional Logic ====================

function isColumnFieldCondition(condition: ColumnCondition): condition is ColumnFieldCondition {
  if (!('column' in condition && 'operator' in condition)) return false;

  return (
    typeof condition.column === 'string' && isValidComparisonOperator((condition as any).operator)
  );
}

function isColumnLogicalCondition(condition: ColumnCondition): condition is ColumnLogicalCondition {
  if (!('operator' in condition && 'conditions' in condition)) return false;

  return (
    isValidLogicalOperator((condition as any).operator) &&
    Array.isArray((condition as any).conditions)
  );
}

function evaluateColumnFieldCondition(
  condition: ColumnFieldCondition,
  rowData: Record<string, any>,
): boolean {
  try {
    const columnValue = rowData[condition.column];

    switch (condition.operator) {
      case 'equals':
        return columnValue === condition.value;

      case 'notEquals':
        return columnValue !== condition.value;

      case 'empty':
        return isEmptyValue(columnValue);

      case 'notEmpty':
        return !isEmptyValue(columnValue);

      case 'contains':
        if (typeof columnValue === 'string' && typeof condition.value === 'string') {
          return columnValue.includes(condition.value);
        }
        return false;

      case 'notContains':
        if (typeof columnValue === 'string' && typeof condition.value === 'string') {
          return !columnValue.toLowerCase().includes(condition.value.toLowerCase());
        }
        if (Array.isArray(condition.value)) {
          return !condition.value.some(value => {
            if (typeof columnValue === 'string' && typeof value === 'string') {
              return columnValue.toLowerCase().includes(value.toLowerCase());
            }
            return columnValue === value;
          });
        }
        return true;

      case 'in':
        if (Array.isArray(condition.value)) {
          return condition.value.some(value => value === columnValue);
        }
        return false;

      case 'notIn':
        if (Array.isArray(condition.value)) {
          return !condition.value.some(value => value === columnValue);
        }
        return true;

      case 'greaterThan': {
        const colNum = toNumber(columnValue);
        const conditionNum = toNumber(condition.value);
        if (colNum === null || conditionNum === null) return false;
        return colNum > conditionNum;
      }

      case 'greaterThanOrEqual': {
        const colNum = toNumber(columnValue);
        const conditionNum = toNumber(condition.value);
        if (colNum === null || conditionNum === null) return false;
        return colNum >= conditionNum;
      }

      case 'lessThan': {
        const colNum = toNumber(columnValue);
        const conditionNum = toNumber(condition.value);
        if (colNum === null || conditionNum === null) return false;
        return colNum < conditionNum;
      }

      case 'lessThanOrEqual': {
        const colNum = toNumber(columnValue);
        const conditionNum = toNumber(condition.value);
        if (colNum === null || conditionNum === null) return false;
        return colNum <= conditionNum;
      }

      default:
        console.warn('Unknown column condition operator:', condition.operator);
        return true;
    }
  } catch (error) {
    console.error('Error evaluating column field condition:', error, condition);
    return true; // Default to showing the column on error
  }
}

/**
 * Evaluates a column condition against a row's data.
 * This is used for table column visibility based on other column values within the same row.
 *
 * @param condition - The column condition to evaluate
 * @param rowData - The data object for the current row (keyed by column ID)
 * @returns boolean - Whether the condition is met
 */
export function evaluateColumnCondition(
  condition: ColumnCondition,
  rowData: Record<string, any>,
): boolean {
  try {
    if (isColumnFieldCondition(condition)) {
      return evaluateColumnFieldCondition(condition, rowData);
    }

    if (isColumnLogicalCondition(condition)) {
      if (condition.operator === 'and') {
        return condition.conditions.every(c => evaluateColumnCondition(c, rowData));
      } else if (condition.operator === 'or') {
        return condition.conditions.some(c => evaluateColumnCondition(c, rowData));
      }
    }

    console.warn('Invalid column condition structure:', condition);
    return true; // Default to showing the column for invalid conditions
  } catch (error) {
    console.error('Error evaluating column condition:', error, condition);
    return true; // Default to showing the column on error
  }
}

/**
 * Checks if a table column should be visible for a specific row.
 * If the column has no conditional behavior, it defaults to visible.
 *
 * @param column - The table column definition
 * @param rowData - The data object for the current row (keyed by column ID)
 * @returns boolean - Whether the column should be visible for this row
 */
export function isTableColumnVisible(
  column: PolicyFormTableColumn,
  rowData: Record<string, any>,
): boolean {
  // No conditional defined means always visible (backward compatible)
  if (!column.conditional?.visible) {
    return true;
  }

  return evaluateColumnCondition(column.conditional.visible, rowData);
}

/**
 * Evaluates all conditional behaviors for a table column within a specific row.
 * Returns visibility and required state based on the row's data.
 *
 * @param column - The table column definition
 * @param rowData - The data object for the current row (keyed by column ID)
 * @returns ColumnConditionalEvaluationResult - Object with visible and required booleans
 */
export function evaluateColumnConditionalBehavior(
  column: PolicyFormTableColumn,
  rowData: Record<string, any>,
): ColumnConditionalEvaluationResult {
  try {
    const defaultRequired = column.required ?? false;

    // No conditional defined - use defaults (backward compatible)
    if (!column.conditional) {
      return {
        visible: true,
        required: defaultRequired,
      };
    }

    // Evaluate visibility
    const visible = column.conditional.visible
      ? evaluateColumnCondition(column.conditional.visible, rowData)
      : true;

    // If not visible, it can't be required
    if (!visible) {
      return {
        visible: false,
        required: false,
      };
    }

    // If visible, evaluate required condition if present
    const required = column.conditional.required
      ? evaluateColumnCondition(column.conditional.required, rowData)
      : defaultRequired;

    return {
      visible,
      required,
    };
  } catch (error) {
    console.error('Error evaluating column conditional behavior:', error, column);
    return {
      visible: true, // Default to visible on error
      required: column.required ?? false,
    };
  }
}
